import { observable, action, computed } from 'mobx';

class TableEditorStore {
  @observable mode = 'edit';
  @observable data = null;
  @observable insertedRowIndex = null;
  @observable insertedColumIndex = null;
  @observable insertRowIndex = null;
  @observable insertColumnIndex = null;
  @observable insertRowType = null;
  @observable insertColumnType = null;
  @observable disableSelect = false;
  @observable columnSelectionRange = [0, 0];
  @observable rowSelectionRange = [0, 0];
  @observable rowSelectionIsInProgress = false;
  @observable columnSelectionIsInProgress = false;
  @observable insertionMarkerIsVisible = false;

  constructor(data) {
    this.data = data;
  }

  @action.bound
  setInsertColumnIndex(index, type) {
    this.insertColumnIndex = index;
    this.insertColumnType = type;
  }

  @action.bound
  setInsertRowIndex(index, type) {
    this.insertRowIndex = index;
    this.insertRowType = type;
  }

  @action.bound
  showInsertionMarker() {
    this.insertionMarkerIsVisible = true;
  }

  @action.bound
  hideInsertionMarker() {
    this.insertionMarkerIsVisible = false;
  }

  @action.bound
  startColumnSelection(start) {
    this.columnSelectionRange = [start, start === 0 ? 1 : start];
    this.rowSelectionRange = [0, this.size.rows - 1];
    this.columnSelectionIsInProgress = true;
  }

  @action.bound
  startRowSelection(start) {
    this.rowSelectionRange = [start, start === 0 ? 1 : start];
    this.columnSelectionRange = [0, this.size.columns - 1];
    this.rowSelectionIsInProgress = true;
  }

  @action.bound
  expandColumnSelection(end) {
    if (!this.columnSelectionIsInProgress) return;
    this.columnSelectionRange[1] = end;
    this.columnSelectionRange.slice().sort();
  }

  @action.bound
  expandRowSelection(end) {
    if (!this.rowSelectionIsInProgress) return;
    this.rowSelectionRange[1] = end;
    this.rowSelectionRange.slice().sort();
  }

  @action.bound
  endColumnSelection() {
    this.columnSelectionIsInProgress = false;
  }

  @action.bound
  endRowSelection() {
    this.rowSelectionIsInProgress = false;
  }

  @action
  setData(data) {
    this.data = data;
  }

  @computed
  get size() {
    return {
      rows: this.data.length,
      columns: this.data[0].length,
    };
  }

  @computed
  get selectedRows() {
    return Array(this.size.rows)
      .fill(false)
      .map((element, i) => this.rowIsSelected(i));
  }

  @computed
  get selectedCols() {
    return Array(this.size.columns)
      .fill(false)
      .map((element, i) => this.columnIsSelected(i));
  }

  @computed
  get selection() {
    return {
      start: {
        i: this.rowSelectionRange[0],
        j: this.columnSelectionRange[0],
      },
      end: {
        i: this.rowSelectionRange[1],
        j: this.columnSelectionRange[1],
      },
    };
  }

  @computed
  get hasFullRowSelected() {
    return (
      Math.abs(this.selection.end.j - this.selection.start.j) ===
      this.size.columns - 1
    );
  }

  @computed
  get hasFullColumnSelected() {
    return (
      Math.abs(this.selection.end.i - this.selection.start.i) ===
      this.size.rows - 1
    );
  }

  @action.bound
  applySelection({ start, end }) {
    this.rowSelectionRange = [start.i, end.i];
    this.columnSelectionRange = [start.j, end.j];
    this.rowSelectionRange.slice().sort();
    this.columnSelectionRange.slice().sort();
  }

  @action.bound
  resetSelection() {
    this.rowSelectionRange = [0, 0];
    this.columnSelectionRange = [0, 0];
  }

  @computed
  get rowIsSelected() {
    const { start, end } = this.selection;
    return index =>
      (index >= start.i && index <= end.i) ||
      (index >= end.i && index <= start.i);
  }

  @computed
  get columnIsSelected() {
    const { start, end } = this.selection;
    return index =>
      (index >= start.j && index <= end.j) ||
      (index >= end.j && index <= start.j);
  }

  @action.bound
  removeSelectedCells() {
    const colFilterFunc = this.hasFullColumnSelected
      ? (f, i) => !this.columnIsSelected(i)
      : () => true;
    const rowFilterFunc = this.hasFullRowSelected
      ? (f, i) => !this.rowIsSelected(i)
      : () => true;

    const newData = this.data

      .map(row => row.filter(colFilterFunc))
      .filter(rowFilterFunc);

    this.data = newData;
    this.resetSelection();
  }

  @action.bound
  insertRow() {
    const newData = this.data;
    const newRow = newData[0].map(() => ({ value: '' }));
    const sign = this.insertRowType === 'after' ? 1 : -1;
    let spliceIndex = this.insertRowIndex + sign;
    if (spliceIndex > newData.length) spliceIndex = newData.length;
    if (spliceIndex < 0) spliceIndex = 0;
    this.insertedRowIndex = spliceIndex;
    newData.splice(spliceIndex, 0, newRow);
    this.data = newData;
  }

  @action.bound
  insertColumn() {
    const newData = this.data.map(row => {
      const newColumn = { value: '' };
      const sign = this.insertColumnType === 'after' ? 1 : -1;
      let spliceIndex = this.insertColumnIndex + sign;
      if (spliceIndex > row.length) spliceIndex = row.length;
      if (spliceIndex < 0) spliceIndex = 0;
      row.splice(spliceIndex, 0, newColumn);
      return row;
    });
    this.data = newData;
  }

  @computed
  get serializedData() {
    const newData = this.data.map(r => r.map(c => ({ ...c })));
    return JSON.stringify(newData);
  }

  @action.bound
  applyChange(cell, rowI, columnI, value) {
    this.data[rowI][columnI] = { value };
  }

  @action.bound
  switchToView() {
    this.mode = 'view';
  }

  @action.bound
  switchToEdit() {
    this.mode = 'edit';
  }
}

export default TableEditorStore;
